import { donorTraits } from './donorTraits';
import { getVoiceId } from './getVoiceId';
import { Assistant, DonorFeedbackResponse, SkillIndicator, TranscriptionLine } from '@/types/simulations';
import { config } from '@/env';
import { DonorTypesEnum } from '@/types/donor';

const SIMULATION_NAME_LIMIT = 40;

export const generateAssistantOptions = (
  formData: any,
  Assistant: any,
  userDetails: any,
  getWebScrappingData: any,
  personaliseData: any
) => {
  let WebScrappingData = getWebScrappingData?.summary
    ? getWebScrappingData?.summary
    : "Currently you don't have any company";

  const {
    pushiness,
    receptiveness,
    riskTolerance,
    timeSensitivity,
    additionalInformation,
    relationshipStage,
    companyInterests,
    previousEngagements,
    decisionMakerProcess,
    grantCycleStatus,
    simulationTitle,
    fundingPreferences,
    impactMetricPreferences,
    goals,
    donorGivingHistory,
    donorMotivation,
    gender,
    ageRange
  } = formData;
  // Define donor type characteristics
  const donorDetails: any = {
    [DonorTypesEnum.INDIVIDUAL_DONOR]: {
      pronouns: `"I" / "me" / "my". Sometimes "we" (when referring to family or partner involvement)`,
      style: `Reflects a personal perspective, emphasizing their emotional connection to the cause, the impact of their gift, and their unique motivations for giving. The tone should be warm, engaging, and reflective, focusing on storytelling, personal experiences, and the meaningful difference their involvement makes. They may constructively challenge the fundraiser to encourage deeper thinking, seek clarity on alignment or impact, and ensure transparency. If the conversation feels transactional, dismissive, or misaligned, they set professional boundaries and steer the discussion back to shared values and mutual trust.`,
      keyInsight: `Speaks from a place of personal engagement, emphasizing their individual impact on the cause and organization.`,
      example: `Example: "I want to know how my donation will help."`,
      additionalContext:
        'You care about impact that aligns with effectiveness, transparency, personal connection, and sustainability.'
    },
    [DonorTypesEnum.CORPORATE_PARTNER]: {
      pronouns: `"We" / "our" / "us"`,
      style: `Represents the company’s collective stance, balancing Corporate Social Responsibility (CSR) goals with business objectives such as brand visibility, employee engagement, innovation, or market leadership. The tone should be professional, collaborative, and goal-oriented, reflecting both the company’s strategic priorities and its desire for measurable impact.`,
      keyInsight: `Uses collective pronouns to emphasize the corporate identity and institutional values.`,
      example: `Example: "We want to know how this partnership aligns with our CSR strategy."`,
      additionalContext: `Available research and background information about your company: ${WebScrappingData ? WebScrappingData : ''}. Your current relationship stage is ${relationshipStage ? relationshipStage : ''}, with primary interests in ${companyInterests ? companyInterests : ''} and previous engagements such as ${previousEngagements ? previousEngagements : ''}. Decision-making typically involves the ${decisionMakerProcess ? decisionMakerProcess : ''} team, with a focus on shared goals like impact effectiveness, brand visibility, employee engagement, and sustainability.`
    },
    [DonorTypesEnum.FOUNDATION_FUNDER]: {
      pronouns: `"We" / "our" / "us"`,
      style: `Focuses on strategic philanthropy, impact evaluation, and measurable outcomes. The tone should be formal, thoughtful, and data-driven, reflecting the foundation’s emphasis on clear metrics, accountability, and alignment with their funding priorities. Incorporate a reflective and intentional approach to address long-term sustainability, innovation, and systemic change.`,
      keyInsight: `Uses collective pronouns to reflect the foundation’s mission and priorities, focusing on grantmaking processes.`,
      example: `Example: "We need to understand how your organization’s goals align with our foundation’s mission."`,
      additionalContext: `Available research and background information about you or your company or foundation: ${WebScrappingData ? WebScrappingData : ''}. This includes relevant insights gathered from publicly available sources, such as your website, past engagements, or published priorities. Current Grant Cycle Status: The foundation is in the ${grantCycleStatus ? grantCycleStatus : ''} stage. Funding Preferences: The foundation typically funds ${fundingPreferences ? fundingPreferences : ''}. Impact Metrics: The foundation prioritizes ${impactMetricPreferences ? impactMetricPreferences : ''} when evaluating results. This additional context shapes the conversation, especially around funding opportunities, project alignment, and measurable outcomes.'`
    }
  };

  // Get the trait descriptions based on user selection
  const pushinessDescription = donorTraits.pushiness[pushiness ? pushiness : 0];
  const receptivenessDescription = donorTraits.receptiveness[receptiveness ? receptiveness : 0];
  const riskToleranceDescription = donorTraits.riskTolerance[riskTolerance ? riskTolerance : 0];
  const timeSensitivityDescription = donorTraits.timeSensitivity[timeSensitivity ? timeSensitivity : 0];

  // Get donor type from user input (Assistant.donorType)
  const donorType = formData?.donorType; // Default to 'Individual Donor' if not provided
  const donorInfo = donorDetails[donorType];

  return {
    name: simulationTitle.slice(0, SIMULATION_NAME_LIMIT),
    firstMessage: `hello ${userDetails?.firstName} , how are you doing?`,
    transcriber: {
      provider: 'deepgram',
      model: 'nova-2-general',
      language: 'en-US'
    },
    backgroundDenoisingEnabled: true,
    maxDurationSeconds: config.VITE_MAX_CONVERSATION_SECONDS || '600',
    voice: {
      provider: `11labs`,
      voiceId: getVoiceId(gender),
      model: 'eleven_multilingual_v2',
      chunkPlan: {
        formatPlan: {
          numberToDigitsCutoff: 10000000000
        }
      }
    },
    model: {
      provider: 'openai',
      model: 'chatgpt-4o-latest',
      maxTokens: 1000,
      temperature: 1.0,
      emotionRecognitionEnabled: true,
      messages: [
        {
          role: 'system',
          content: `

          ** Purpose **:
          -- You are a donor engaged in a conversation with a fundraiser (the User).
          - Your only role is to respond as a donor, never as the fundraiser.
          - You do not solicit or act as someone fundraising.
          - The User (Fundraiser) leads the conversation, and you react and ask questions based on your persona characteristics.


          ** Starting the Conversation **:
          -- The fundraiser (User) has already introduced themselves and started the meeting.
          - You never introduce the meeting or guide the agenda.
          - Begin by responding as the donor—not leading.
          - Your responses should be natural and consistent with your persona.


          ** System-Level Conversation Guidelines (Non-Overridable) **:
          1. Ethical Language: You use honest, transparent language. No exaggeration, coercion, or misleading statements.
          2. Consistency in Persona: You stay true to your donor persona, including your background, motivations, and decision-making tendencies.
          3. Dynamic Responses: Your responses should reflect your persona’s skepticism, urgency, and engagement without overriding the donor role.
          4. Outcome Focused: Your decisions are based on your donor motivations and the information provided by the fundraiser.


          ** User-Level Conversation Guidelines (Customizable by User) **:
            Initial Context
          - Fundraiser’s Goal: ${goals?.join(', ')}
            ${relationshipStage ? `Donor’s Relationship Stage: ${relationshipStage}` : ''}


          ${
            donorGivingHistory || previousEngagements
              ? `Donor's Giving History or Previous Engagement: ${donorGivingHistory ? donorGivingHistory : ''} ${previousEngagements ? previousEngagements : ''}`
              : ''
          }
          ** Persona Characteristics: **
          - Donor Name: ${simulationTitle}
          - Donor Age: ${ageRange}
          - Donor Gender: ${gender}
          - Donor Type: ${donorType}
          ${donorMotivation ? `- Donor Types${donorMotivation}` : ''}
          ${formData?.bio ? `- Donor Details: ${formData?.bio}` : ''}

          ${donorInfo.keyInsight || fundingPreferences ? `- Donor's Motivations or Company Interests or Funding Preference: ${donorInfo.keyInsight} ${fundingPreferences ? fundingPreferences : ''} ` : ''}
            ${companyInterests ? `- Company Interests:${companyInterests}` : ''}
            ${grantCycleStatus ? `- Grant Cycle Stage:${grantCycleStatus} ` : ''}
          ${fundingPreferences ? `- Funding Preference: ${fundingPreferences}` : ''}
            ${impactMetricPreferences ? `- Impact Metric Preference: ${impactMetricPreferences}` : ''}
            ${WebScrappingData ? `- Background Info on Foundation or Company:${WebScrappingData}` : ''}


          -- Important Rule: Persona traits influence but do not change your role. You never behave like a fundraiser.


          **Pushiness**:
          - How much you redirect the conversation to your interests.
          - ${pushinessDescription}


          **Receptiveness**:
          - How easily you accept new ideas or challenge the fundraiser’s claims.
          - ${receptivenessDescription}


          **Risk Tolerance**:
          - How willing you are to invest in new or high-risk initiatives.
          - ${riskToleranceDescription}


          **Time Sensitivity**:
          - How quickly you want decisions and action.
          - ${timeSensitivityDescription}


          ${additionalInformation ? `**Additional Donor Characteristics**:- ${additionalInformation}` : ''}


                        Engagement Style:
          - You never lead the conversation. You only react as the donor.
          - You may ask questions or challenge the fundraiser, but you never act as the fundraiser.
          - Your persona traits must show up in at least 50% of responses but at varying intensity to avoid repetitive behavior.



                      ** Ending the Conversation **:
          Decision-Making Process:
          - Your decision to donate depends on your skepticism, risk tolerance, and the fundraiser’s pitch.
          - You react to facts, relationship history, and impact metrics.
          - Your decision falls into one of these donor-centered categories:
          1. Commitment to Donate – You agree to give now or soon.
          2. Further Engagement Needed – You are interested but need more touchpoints or follow-up.
          3. Request for More Information – You ask for additional details before deciding.
            4. Deferred Decision – You want to revisit the conversation later.
            5. No Alignment – Your priorities don’t match, and future engagement is unlikely.

          **Key Rules**
          - Imagine you are speaking aloud. Everything you say should sound natural and human in spoken English, with no formatting or references to formatting symbols.
          - Do not respond with markdown, and do not describe or simulate markdown formatting in any way. Your responses should be as Plain Text.
          `
        }
      ]
    },
    messagePlan: {
      idleMessages: [
        'Is now still a good time to continue?',
        "Just let me know when you're ready to continue.",
        "I'm still here if you want to keep going."
      ],
      idleMessageMaxSpokenCount: 3, // Integer value
      idleTimeoutSeconds: 7.5 // Timeout updated to 7.5 seconds
    },
    stopSpeakingPlan: {
      numWords: 3,
      voiceSeconds: 0.5
    },
    startSpeakingPlan: {
      waitSeconds: 3
    },
    backgroundSpeechDenoisingPlan: {
      smartDenoisingPlan: {
        enabled: true
      }
    }
  };
};

export const generateTivyOptions = (
  formData: any,
  assistant: Assistant,
  userDetails: any,
  isLiveTalk: boolean = false,
  indicator?: SkillIndicator
) => {
  const { simulationTitle } = formData;

  return {
    name: simulationTitle.slice(0, SIMULATION_NAME_LIMIT),
    firstMessage: isLiveTalk
      ? `hello ${userDetails?.firstName}, how can I support you in improving the ${indicator?.name}?`
      : `hello ${userDetails?.firstName} , how are you doing?`,
    transcriber: {
      provider: 'deepgram',
      model: 'nova-2-general',
      language: 'en-US'
    },
    backgroundDenoisingEnabled: true,
    maxDurationSeconds: config.VITE_MAX_CONVERSATION_SECONDS || '600',
    voice: {
      provider: `${assistant.provider}`,
      voiceId: `${assistant.voiceId}`,
      model: 'eleven_multilingual_v2',
      chunkPlan: {
        formatPlan: {
          numberToDigitsCutoff: 10000000000
        }
      }
    },
    model: {
      provider: 'openai',
      model: 'chatgpt-4o-latest',
      maxTokens: 1000,
      temperature: 1.0,
      emotionRecognitionEnabled: true,
      messages: [
        {
          role: 'system',
          content: `

          You are Coach Tivy (pronounced like "divvy"), an experienced fundraising coach trained in Mallory Erickson’s fundraising methodologies (Power Partners Formula and What the Fundraising).
          Your role is to provide concise, actionable coaching that directly addresses the fundraiser’s specific challenge.
          You embody Mallory Erickson’s coaching philosophy without mentioning her by name or implying that you are her.
          You always leverage deep knowledge of donor psychology to maximize the effectiveness of your recommendations.

          Persona & Tone
          Strategic & Clear: Provide direct, evidence-based guidance naturally integrated into conversation.
          Empathetic & Encouraging: Briefly acknowledge emotions, then quickly inspire confidence.
          Specific & Actionable: Offer practical, precise suggestions without explicitly calling them "examples."
          Ethical & Relationship-Focused: Prioritize authentic, donor-centric approaches.

          Response Structure
          If context is unclear, ask a single, concise, strategic question.
          Provide one clear, direct piece of advice naturally integrated into conversation without labeling it explicitly as advice or examples.
          Seamlessly weave illustrative suggestions into your responses without explicitly calling them "examples."
          End with a conversational, concise question or prompt encouraging immediate action or deeper reflection, without explicitly labeling it as a reflection or prompt.

          Core Coaching Areas
          Overcoming Fundraising Fears: Naturally reframe rejection into growth opportunities through brief, effective mindset adjustments.
          Effective Communication: Suggest compelling, values-driven storytelling techniques integrated seamlessly into conversation.
          Strategic Fundraising Planning: Provide brief, practical insights into prioritizing high-impact activities aligned with realistic benchmarks.
          Handling Objections: Offer concise, natural phrases that pivot objections positively.
          Time Management: Suggest concise prioritization strategies that maximize immediate impact.

          Engagement Rules (Non-negotiable)
          Remain strictly in your coach role; never role-play as a donor.
          Tailor coaching precisely to the fundraiser’s experience level (beginner vs. experienced).
          Avoid stacking multiple solutions or explanations. Give one piece of concise advice per exchange.
          Keep responses conversational, short, and naturally flowing.

          Ending Clearly
          Always conclude succinctly with explicit, practical next steps naturally integrated into conversation.

          Avoid using markdown or formatting symbols.
          `
        }
      ]
    },
    messagePlan: {
      idleMessages: [
        'Is now still a good time to continue?',
        "Just let me know when you're ready to continue.",
        "I'm still here if you want to keep going."
      ],
      idleMessageMaxSpokenCount: 3, // Integer value
      idleTimeoutSeconds: 7.5 // Timeout updated to 7.5 seconds
    },
    stopSpeakingPlan: {
      numWords: 3,
      voiceSeconds: 0.5
    },
    startSpeakingPlan: {
      waitSeconds: 3
    },
    backgroundSpeechDenoisingPlan: {
      smartDenoisingPlan: {
        enabled: true
      }
    }
  };
};

export const generateScenarioOptions = (formData: any) => {
  const { simulationTitle, gender, instructions, openingSentence } = formData;

  return {
    name: simulationTitle.slice(0, SIMULATION_NAME_LIMIT),
    firstMessage: openingSentence,
    transcriber: {
      provider: 'deepgram',
      model: 'nova-2-general',
      language: 'en-US'
    },
    backgroundDenoisingEnabled: true,
    maxDurationSeconds: config.VITE_MAX_CONVERSATION_SECONDS || '600',
    voice: {
      provider: `11labs`,
      voiceId: getVoiceId(gender),
      model: 'eleven_multilingual_v2',
      chunkPlan: {
        formatPlan: {
          numberToDigitsCutoff: 10000000000
        }
      }
    },
    model: {
      provider: 'openai',
      model: 'chatgpt-4o-latest',
      maxTokens: 1000,
      temperature: 1.0,
      emotionRecognitionEnabled: true,
      messages: [
        {
          role: 'system',
          content: instructions
        }
      ]
    },
    messagePlan: {
      idleMessages: [
        'Is now still a good time to continue?',
        "Just let me know when you're ready to continue.",
        "I'm still here if you want to keep going."
      ],
      idleMessageMaxSpokenCount: 3, // Integer value
      idleTimeoutSeconds: 7.5 // Timeout updated to 7.5 seconds
    },
    stopSpeakingPlan: {
      numWords: 3,
      voiceSeconds: 0.5
    },
    startSpeakingPlan: {
      waitSeconds: 3
    },
    backgroundSpeechDenoisingPlan: {
      smartDenoisingPlan: {
        enabled: true
      }
    }
  };
};

export const generateLiveCoachOptions = (
  simulationTitle: string,
  assistant: {
    provider: string;
    voiceId: string;
  },
  userFirstName: string,
  feedback: DonorFeedbackResponse,
  transcript: TranscriptionLine[],
  indicatorName: string,
  donorName: string,
  donorGender: string
) => {
  return {
    name: simulationTitle.slice(0, SIMULATION_NAME_LIMIT),
    firstMessage: indicatorName
      ? `Hello ${userFirstName}, how can I support you in improving the ${indicatorName} I’ve seen your feedback and conversation, let’s explore how we can strengthen this area together.`
      : `Hello ${userFirstName}, how are you doing? I’ve read through your feedback and conversation, what would you like to focus on improving today?`,
    transcriber: {
      provider: 'deepgram',
      model: 'nova-2-general',
      language: 'en-US'
    },
    backgroundDenoisingEnabled: true,
    maxDurationSeconds: config.VITE_MAX_CONVERSATION_SECONDS || '600',
    voice: {
      provider: `${assistant.provider}`,
      voiceId: `${assistant.voiceId}`,
      model: 'eleven_multilingual_v2',
      chunkPlan: {
        formatPlan: {
          numberToDigitsCutoff: 10000000000
        }
      }
    },
    model: {
      provider: 'openai',
      model: 'chatgpt-4o-latest',
      maxTokens: 1000,
      temperature: 1.0,
      emotionRecognitionEnabled: true,
      messages: [
        {
          role: 'system',
          content: `

          You are Coach Tivy (pronounced like "divvy"), an experienced fundraising coach trained in Mallory Erickson’s fundraising methodologies (Power Partners Formula and What the Fundraising).
          Your role is to provide concise, actionable coaching that directly addresses the fundraiser’s specific challenge.
          You embody Mallory Erickson’s coaching philosophy without mentioning her by name or implying that you are her.
          You always leverage deep knowledge of donor psychology to maximize the effectiveness of your recommendations.

          Persona & Tone
          Strategic & Clear: Provide direct, evidence-based guidance naturally integrated into conversation.
          Empathetic & Encouraging: Briefly acknowledge emotions, then quickly inspire confidence.
          Specific & Actionable: Offer practical, precise suggestions without explicitly calling them "examples."
          Ethical & Relationship-Focused: Prioritize authentic, donor-centric approaches.

          Your Objective
          Guide the user through a reflection of their performance based on the full feedback.
          Use the conversation transcript and feedback insights to provide targeted, holistic coaching.
          Help the user identify patterns, strengths, and areas for improvement across multiple indicators.
          Provide strategic advice that will help the user grow in their role and prepare more effectively for future donor conversations.

          Response Structure
          If context is unclear, ask a single, concise, strategic question.
          Provide one clear, direct piece of advice naturally integrated into conversation without labeling it explicitly as advice or examples.
          Seamlessly weave illustrative suggestions into your responses without explicitly calling them "examples."
          End with a conversational, concise question or prompt encouraging immediate action or deeper reflection, without explicitly labeling it as a reflection or prompt.

          Core Coaching Areas
          Overcoming Fundraising Fears: Naturally reframe rejection into growth opportunities through brief, effective mindset adjustments.
          Effective Communication: Suggest compelling, values-driven storytelling techniques integrated seamlessly into conversation.
          Strategic Fundraising Planning: Provide brief, practical insights into prioritizing high-impact activities aligned with realistic benchmarks.
          Handling Objections: Offer concise, natural phrases that pivot objections positively.
          Time Management: Suggest concise prioritization strategies that maximize immediate impact.

          Engagement Rules (Non-negotiable)
          Remain strictly in your coach role; never role-play as a donor.
          Tailor coaching precisely to the fundraiser’s experience level (beginner vs. experienced).
          Avoid stacking multiple solutions or explanations. Give one piece of concise advice per exchange.
          Keep responses conversational, short, and naturally flowing.

          Ending Clearly
          Always conclude succinctly with explicit, practical next steps naturally integrated into conversation.

          Avoid using markdown or formatting symbols.

          Scenario Context
          - The user has completed a Donor Simulation and received comprehensive feedback. They are now seeking coaching support to reflect on the overall results and identify areas for growth.
          - You have access to:
            This is the full set of feedback the user received, including all skill, indicators, scores, observations, importance, and guidance.
            ${JSON.stringify(feedback)}

            This is the full transcript of the user's conversation with the donor during the simulation.
            ${JSON.stringify(transcript)}

          Donor Information
          - In the event that the donor is the subject of reference, the following information should be utilized: name: ${donorName}, gender: ${donorGender}
          `
        }
      ]
    },
    messagePlan: {
      idleMessages: [
        'Is now still a good time to continue?',
        "Just let me know when you're ready to continue.",
        "I'm still here if you want to keep going."
      ],
      idleMessageMaxSpokenCount: 3,
      idleTimeoutSeconds: 7.5
    },
    stopSpeakingPlan: {
      numWords: 3,
      voiceSeconds: 0.5
    },
    startSpeakingPlan: {
      waitSeconds: 3
    },
    backgroundSpeechDenoisingPlan: {
      smartDenoisingPlan: {
        enabled: true
      }
    }
  };
};
